// =============================================================================
// Storage utility for token management and temporary UI state
// =============================================================================
// All persistent user progress data comes from API
// localStorage only used for: auth tokens + temporary UI state management

const STORAGE_KEYS = {
  AUTH_TOKEN: 'accessToken',
  TOKEN_EXPIRY: 'yda_tokenExpiry',
  // Temporary UI state keys (not for persistence - only for immediate UI responsiveness)
  CURRENT_LANGUAGE: 'language',
  AUDIO_COMPLETED: 'yda_audioCompleted',
  QUIZ_STATE: 'yda_quizState',
  // Progress tracking for API calls
  COMPLETED_SLIDES: 'completedSlides', // Array format: [1, 2, 3, 4, 5]
  ALL_QUIZ_STATUSES: 'allQuizStatuses', // Object format: {5: {attempts: 2, completed: true, passed: true}}
  IS_ALL_SLIDES_COMPLETED: 'isallSlidesCompleted' // Boolean: true when all slides are completed
};

export const storage = {
  
  // =============================================================================
  // MODULE SCOPING FOR CROSS-TAB ISOLATION
  // =============================================================================
  
  // Current module ID for scoping (null = backward compatibility mode)
  _currentModuleId: null,
  
  // Initialize storage with module ID for scoping
  initialize(moduleId) {
    if (!moduleId) {
      console.warn('Storage: initialize called without moduleId - using backward compatibility mode');
      return;
    }
    
    this._currentModuleId = moduleId;
    console.log(`Storage: Initialized for module ${moduleId}`);
    
    // Migrate old data to module-scoped keys for existing users
    this._migrateOldDataToModuleScoped(moduleId);
  },
  
  // Get current module ID
  getCurrentModuleId() {
    return this._currentModuleId;
  },
  
  // Helper: Get scoped key name
  _getScopedKey(key) {
    // Auth tokens are always shared across modules (user-specific)
    const sharedKeys = [STORAGE_KEYS.AUTH_TOKEN, STORAGE_KEYS.TOKEN_EXPIRY];
    if (sharedKeys.includes(key)) {
      return key;
    }
    
    // If moduleId is set, use module-scoped keys
    if (this._currentModuleId) {
      return `module_${this._currentModuleId}_${key}`;
    }
    
    // Fallback for backward compatibility (when moduleId not set)
    return key;
  },
  
  // Migrate old data to module-scoped keys (for existing users)
  _migrateOldDataToModuleScoped(moduleId) {
    if (!moduleId) return;
    
    const keysToMigrate = [
      STORAGE_KEYS.COMPLETED_SLIDES,
      STORAGE_KEYS.ALL_QUIZ_STATUSES,
      STORAGE_KEYS.AUDIO_COMPLETED,
      STORAGE_KEYS.QUIZ_STATE,
      STORAGE_KEYS.IS_ALL_SLIDES_COMPLETED,
      STORAGE_KEYS.CURRENT_LANGUAGE
    ];
    
    let migrated = false;
    keysToMigrate.forEach(oldKey => {
      const oldValue = localStorage.getItem(oldKey);
      if (oldValue) {
        const newKey = `module_${moduleId}_${oldKey}`;
        // Only migrate if new key doesn't exist (don't overwrite existing module data)
        if (!localStorage.getItem(newKey)) {
          localStorage.setItem(newKey, oldValue);
          migrated = true;
          console.log(`Storage: Migrated ${oldKey} to ${newKey}`);
        }
      }
    });
    
    if (migrated) {
      console.log(`Storage: Migration completed for module ${moduleId}`);
    }
  },
  
  // =============================================================================
  // TOKEN MANAGEMENT FUNCTIONS (UNCHANGED - always shared)
  // =============================================================================

  // Get authentication token from localStorage
  // Returns the token if it exists and hasn't expired, otherwise returns null
  getAuthToken() {
    try {
      const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
      const expiry = localStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRY);
      
      // Check if token exists
      if (!token) {
        console.log('Storage: No auth token found');
        return null;
      }
      
      // Check if token has expired
      if (expiry && new Date().getTime() > parseInt(expiry)) {
        console.log('Storage: Auth token has expired, removing...');
        this.clearAuthToken();
        return null;
      }
      
      console.log('Storage: Valid auth token found');
      return token;
    } catch (error) {
      console.error('Error reading auth token:', error);
      return null;
    }
  },

  // Save authentication token to localStorage with expiry time
  // token: The JWT access token to save
  // expiryInHours: How many hours until the token expires (default: 24 hours)
  setAuthToken(token, expiryInHours = 24) {
    try {
      if (!token) {
        console.error('Storage: Cannot save empty token');
        return;
      }
      
      // Calculate expiry time (current time + hours in milliseconds)
      const expiry = new Date().getTime() + (expiryInHours * 60 * 60 * 1000);
      
      localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
      localStorage.setItem(STORAGE_KEYS.TOKEN_EXPIRY, expiry.toString());
      
      console.log(`Storage: Auth token saved (expires in ${expiryInHours} hours)`);
    } catch (error) {
      console.error('Error saving auth token:', error);
    }
  },

  // Remove authentication token from localStorage
  clearAuthToken() {
    try {
      localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.TOKEN_EXPIRY);
      console.log('Storage: Auth token cleared');
    } catch (error) {
      console.error('Error clearing auth token:', error);
    }
  },

  // Check if we have a valid authentication token
  // Returns true if token exists and hasn't expired
  hasValidToken() {
    return this.getAuthToken() !== null;
  },

  // =============================================================================
  // TEMPORARY UI STATE FUNCTIONS (for immediate UI responsiveness only)
  // =============================================================================
  // These are NOT for persistence - all real data comes from API
  // Only used for immediate UI state management while API calls are in progress

  // Get current language preference (temporary UI state) - now module-scoped
  getCurrentLanguage() {
    try {
      const key = this._getScopedKey(STORAGE_KEYS.CURRENT_LANGUAGE);
      return localStorage.getItem(key) || 'en';
    } catch (error) {
      console.error('Error reading current language:', error);
      return 'en';
    }
  },

  // Set current language preference (temporary UI state) - now module-scoped
  setCurrentLanguage(languageCode) {
    try {
      const key = this._getScopedKey(STORAGE_KEYS.CURRENT_LANGUAGE);
      localStorage.setItem(key, languageCode);
    } catch (error) {
      console.error('Error saving current language:', error);
    }
  },

  // Get audio completed state (temporary UI state) - now module-scoped
  getAudioCompleted() {
    try {
      const key = this._getScopedKey(STORAGE_KEYS.AUDIO_COMPLETED);
      const stored = localStorage.getItem(key);
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      console.error('Error reading audio completed state:', error);
      return {};
    }
  },

  // Set audio completed for a specific slide (temporary UI state) - now module-scoped
  setAudioCompleted(slideId, completed = true) {
    try {
      const audioCompleted = this.getAudioCompleted();
      audioCompleted[slideId] = completed;
      const key = this._getScopedKey(STORAGE_KEYS.AUDIO_COMPLETED);
      localStorage.setItem(key, JSON.stringify(audioCompleted));
    } catch (error) {
      console.error('Error saving audio completed state:', error);
    }
  },

  // Check if audio is completed for a specific slide (temporary UI state)
  isAudioCompleted(slideId) {
    try {
      const audioCompleted = this.getAudioCompleted();
      return audioCompleted[slideId] === true;
    } catch (error) {
      console.error('Error checking audio completed state:', error);
      return false;
    }
  },

  // Get quiz state for a specific slide (temporary UI state) - now module-scoped
  getSlideQuizState(slideId) {
    try {
      const key = this._getScopedKey(STORAGE_KEYS.QUIZ_STATE);
      const stored = localStorage.getItem(key);
      const quizState = stored ? JSON.parse(stored) : {};
      return quizState[slideId] || {
        attempts: 0,
        completed: false,
        passed: false
      };
    } catch (error) {
      console.error('Error reading quiz state:', error);
      return {
        attempts: 0,
        completed: false,
        passed: false
      };
    }
  },

  // Set quiz state for a specific slide (temporary UI state) - now module-scoped
  setSlideQuizState(slideId, state) {
    try {
      const key = this._getScopedKey(STORAGE_KEYS.QUIZ_STATE);
      const stored = localStorage.getItem(key);
      const quizState = stored ? JSON.parse(stored) : {};
      quizState[slideId] = {
        ...this.getSlideQuizState(slideId),
        ...state
      };
      localStorage.setItem(key, JSON.stringify(quizState));
    } catch (error) {
      console.error('Error saving quiz state:', error);
    }
  },

  // =============================================================================
  // PROGRESS TRACKING FUNCTIONS (for API calls)
  // =============================================================================

  // Get completed slides array in format: [1, 2, 3, 4, 5] - now module-scoped
  getCompletedSlides() {
    try {
      const key = this._getScopedKey(STORAGE_KEYS.COMPLETED_SLIDES);
      const stored = localStorage.getItem(key);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error reading completed slides:', error);
      return [];
    }
  },

  // Set completed slides array in format: [1, 2, 3, 4, 5] - now module-scoped
  setCompletedSlides(slidesArray) {
    try {
      if (!Array.isArray(slidesArray)) {
        console.error('Error: completedSlides must be an array');
        return;
      }
      const key = this._getScopedKey(STORAGE_KEYS.COMPLETED_SLIDES);
      localStorage.setItem(key, JSON.stringify(slidesArray));
      const moduleInfo = this._currentModuleId ? ` for module ${this._currentModuleId}` : '';
      console.log(`Storage: Completed slides updated${moduleInfo}:`, slidesArray);
    } catch (error) {
      console.error('Error saving completed slides:', error);
    }
  },

  // Add a slide to completed slides array
  addCompletedSlide(slideNumber) {
    try {
      const completedSlides = this.getCompletedSlides();
      const slideNum = parseInt(slideNumber);
      
      if (!completedSlides.includes(slideNum)) {
        completedSlides.push(slideNum);
        completedSlides.sort((a, b) => a - b); // Keep sorted
        this.setCompletedSlides(completedSlides);
      }
    } catch (error) {
      console.error('Error adding completed slide:', error);
    }
  },

  // Get all quiz statuses in format: {5: {attempts: 2, status: "passed"}} - now module-scoped
  getAllQuizStatuses() {
    try {
      const key = this._getScopedKey(STORAGE_KEYS.ALL_QUIZ_STATUSES);
      const stored = localStorage.getItem(key);
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      console.error('Error reading all quiz statuses:', error);
      return {};
    }
  },

  // Set all quiz statuses in format: {5: {attempts: 2, status: "passed"}} - now module-scoped
  setAllQuizStatuses(quizStatuses) {
    try {
      if (typeof quizStatuses !== 'object' || Array.isArray(quizStatuses)) {
        console.error('Error: allQuizStatuses must be an object');
        return;
      }
      const key = this._getScopedKey(STORAGE_KEYS.ALL_QUIZ_STATUSES);
      localStorage.setItem(key, JSON.stringify(quizStatuses));
      const moduleInfo = this._currentModuleId ? ` for module ${this._currentModuleId}` : '';
      console.log(`Storage: All quiz statuses updated${moduleInfo}:`, quizStatuses);
    } catch (error) {
      console.error('Error saving all quiz statuses:', error);
    }
  },

  // Update quiz status for a specific slide
  updateQuizStatus(slideNumber, attempts, status, completed = false, passed = false) {
    try {
      const allQuizStatuses = this.getAllQuizStatuses();
      allQuizStatuses[slideNumber] = {
        attempts: attempts,
        status: status, // "passed", "failed", "in_progress"
        completed: completed,
        passed: passed
      };
      this.setAllQuizStatuses(allQuizStatuses);
    } catch (error) {
      console.error('Error updating quiz status:', error);
    }
  },

  // Enhanced function to merge API quiz statuses with local storage - now module-scoped
  // This ensures we don't lose any local progress while incorporating API data
  mergeApiQuizStatuses(apiQuizStatuses) {
    try {
      const localQuizStatuses = this.getAllQuizStatuses();
      const mergedStatuses = { ...localQuizStatuses };
      
      // Merge API data, preferring API data for completed quizzes
      Object.keys(apiQuizStatuses).forEach(slideNumber => {
        const apiData = apiQuizStatuses[slideNumber];
        const localData = localQuizStatuses[slideNumber];
        
        // If API has more recent or complete data, use it
        if (apiData && (apiData.completed || !localData)) {
          mergedStatuses[slideNumber] = {
            attempts: apiData.attempts || 0,
            status: apiData.status || 'pending',
            completed: apiData.completed || false,
            passed: apiData.passed || false,
            // Include any additional API fields
            ...apiData
          };
        }
      });
      
      const moduleInfo = this._currentModuleId ? ` for module ${this._currentModuleId}` : '';
      console.log(`Storage: Merged API quiz statuses with local data${moduleInfo}:`, {
        apiQuizStatuses,
        localQuizStatuses,
        mergedStatuses
      });
      
      this.setAllQuizStatuses(mergedStatuses);
      return mergedStatuses;
    } catch (error) {
      console.error('Error merging API quiz statuses:', error);
      return this.getAllQuizStatuses();
    }
  },

  // Function to completely override localStorage with API quiz statuses - now module-scoped
  // This replaces all existing localStorage data with fresh API data
  overrideWithApiQuizStatuses(apiQuizStatuses) {
    try {
      const moduleInfo = this._currentModuleId ? ` for module ${this._currentModuleId}` : '';
      console.log(`Storage: Overriding localStorage with API quiz statuses${moduleInfo}:`, apiQuizStatuses);
      
      // Directly set the API data, completely replacing localStorage
      this.setAllQuizStatuses(apiQuizStatuses || {});
      
      console.log(`Storage: Successfully overridden quiz statuses with API data${moduleInfo}`);
      return apiQuizStatuses || {};
    } catch (error) {
      console.error('Error overriding with API quiz statuses:', error);
      return this.getAllQuizStatuses();
    }
  },

  // Enhanced function to merge API completed slides with local storage - now module-scoped
  mergeApiCompletedSlides(apiCompletedSlides) {
    try {
      const localCompletedSlides = this.getCompletedSlides();
      const apiSlidesArray = Array.isArray(apiCompletedSlides) ? apiCompletedSlides : [];
      
      // Merge arrays and remove duplicates
      const mergedSlides = [...new Set([...localCompletedSlides, ...apiSlidesArray])];
      mergedSlides.sort((a, b) => a - b);
      
      const moduleInfo = this._currentModuleId ? ` for module ${this._currentModuleId}` : '';
      console.log(`Storage: Merged API completed slides with local data${moduleInfo}:`, {
        apiCompletedSlides: apiSlidesArray,
        localCompletedSlides,
        mergedSlides
      });
      
      this.setCompletedSlides(mergedSlides);
      return mergedSlides;
    } catch (error) {
      console.error('Error merging API completed slides:', error);
      return this.getCompletedSlides();
    }
  },

  // Function to completely override localStorage with API completed slides - now module-scoped
  // This replaces all existing localStorage data with fresh API data
  overrideWithApiCompletedSlides(apiCompletedSlides) {
    try {
      const apiSlidesArray = Array.isArray(apiCompletedSlides) ? apiCompletedSlides : [];
      apiSlidesArray.sort((a, b) => a - b);
      
      const moduleInfo = this._currentModuleId ? ` for module ${this._currentModuleId}` : '';
      console.log(`Storage: Overriding localStorage with API completed slides${moduleInfo}:`, apiSlidesArray);
      
      // Directly set the API data, completely replacing localStorage
      this.setCompletedSlides(apiSlidesArray);
      
      console.log(`Storage: Successfully overridden completed slides with API data${moduleInfo}`);
      return apiSlidesArray;
    } catch (error) {
      console.error('Error overriding with API completed slides:', error);
      return this.getCompletedSlides();
    }
  },

  // Get isallSlidesCompleted flag - now module-scoped
  // Returns true if all slides are completed, false otherwise
  // Once true, it should always remain true
  getIsAllSlidesCompleted() {
    try {
      const key = this._getScopedKey(STORAGE_KEYS.IS_ALL_SLIDES_COMPLETED);
      const stored = localStorage.getItem(key);
      return stored === 'true';
    } catch (error) {
      console.error('Error reading isallSlidesCompleted:', error);
      return false;
    }
  },

  // Set isallSlidesCompleted flag - now module-scoped
  // If it's already true, it will remain true (never reset to false)
  setIsAllSlidesCompleted(value) {
    try {
      // If it's already true, don't allow setting it back to false
      const currentValue = this.getIsAllSlidesCompleted();
      if (currentValue === true && value === false) {
        const moduleInfo = this._currentModuleId ? ` for module ${this._currentModuleId}` : '';
        console.log(`Storage: isallSlidesCompleted is already true${moduleInfo}, keeping it as true`);
        return;
      }
      
      const key = this._getScopedKey(STORAGE_KEYS.IS_ALL_SLIDES_COMPLETED);
      localStorage.setItem(key, value ? 'true' : 'false');
      const moduleInfo = this._currentModuleId ? ` for module ${this._currentModuleId}` : '';
      console.log(`Storage: isallSlidesCompleted updated${moduleInfo}:`, value);
    } catch (error) {
      console.error('Error saving isallSlidesCompleted:', error);
    }
  },

  // Clear all progress data for current module (or fallback to old keys if no moduleId)
  clearProgressData() {
    try {
      if (!this._currentModuleId) {
        console.warn('Storage: Cannot clear progress data - no module ID set, clearing old keys as fallback');
        // Fallback: clear old keys for backward compatibility
        localStorage.removeItem(STORAGE_KEYS.COMPLETED_SLIDES);
        localStorage.removeItem(STORAGE_KEYS.ALL_QUIZ_STATUSES);
        localStorage.removeItem(STORAGE_KEYS.AUDIO_COMPLETED);
        localStorage.removeItem(STORAGE_KEYS.QUIZ_STATE);
        localStorage.removeItem(STORAGE_KEYS.IS_ALL_SLIDES_COMPLETED);
        localStorage.removeItem(STORAGE_KEYS.CURRENT_LANGUAGE);
        console.log('Storage: All progress data cleared (backward compatibility mode)');
        return;
      }
      
      // Clear module-scoped keys
      const keysToRemove = [
        STORAGE_KEYS.COMPLETED_SLIDES,
        STORAGE_KEYS.ALL_QUIZ_STATUSES,
        STORAGE_KEYS.AUDIO_COMPLETED,
        STORAGE_KEYS.QUIZ_STATE,
        STORAGE_KEYS.IS_ALL_SLIDES_COMPLETED,
        STORAGE_KEYS.CURRENT_LANGUAGE
      ];
      
      keysToRemove.forEach(key => {
        const scopedKey = this._getScopedKey(key);
        localStorage.removeItem(scopedKey);
      });
      
      console.log(`Storage: All progress data cleared for module ${this._currentModuleId}`);
    } catch (error) {
      console.error('Error clearing progress data:', error);
    }
  },
  
  // Clear all data for a specific module (useful for cleanup)
  clearModuleData(moduleId) {
    try {
      if (!moduleId) {
        console.error('Storage: Cannot clear module data - moduleId is required');
        return;
      }
      
      const modulePrefix = `module_${moduleId}_`;
      const keysToRemove = [
        STORAGE_KEYS.COMPLETED_SLIDES,
        STORAGE_KEYS.ALL_QUIZ_STATUSES,
        STORAGE_KEYS.AUDIO_COMPLETED,
        STORAGE_KEYS.QUIZ_STATE,
        STORAGE_KEYS.IS_ALL_SLIDES_COMPLETED,
        STORAGE_KEYS.CURRENT_LANGUAGE
      ];
      
      keysToRemove.forEach(key => {
        localStorage.removeItem(`${modulePrefix}${key}`);
      });
      
      console.log(`Storage: Cleared all data for module ${moduleId}`);
    } catch (error) {
      console.error('Error clearing module data:', error);
    }
  }
};